import type { KeyEventArgs, MouseEventArgs } from "../core/events.js";
import { clamp, rectContains, type Rect, type Size } from "../../common/geometry.js";
import { visibleRowRange } from "../core/virtualWindow.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme, VisualState } from "../themes/Theme.js";
import { ScrollableBase, type ScrollableOptions } from "./ScrollableBase.js";

export type SelectionMode = "none" | "single" | "multiple";

export interface ListBoxOptions<T = unknown> extends ScrollableOptions {
  items?: readonly T[];
  selectionMode?: SelectionMode;
  selectedIndex?: number;
  itemHeight?: number;
  font?: FontSpec;
  /** Text for an item; defaults to String(item). */
  itemText?: (item: T, index: number) => string;
  onSelectionChanged?: () => void;
}

/**
 * A scrollable list of items with single or multiple selection.
 *
 * Painting is virtualized: only the rows intersecting the viewport are drawn,
 * so a list of 100k strings costs the same per frame as one of 20.
 */
export class ListBox<T = unknown> extends ScrollableBase {
  #items: T[];
  #selected = new Set<number>();
  /** Anchor for shift-extended range selection. */
  #anchor = -1;
  #focusIndex = -1;
  #hoverIndex = -1;
  #itemHeight: number | null;
  #font: FontSpec | null;
  #maxTextWidth = -1;

  selectionMode: SelectionMode;
  itemText: (item: T, index: number) => string;

  constructor(options: ListBoxOptions<T> = {}) {
    super({ width: 240, height: 160, tabStop: true, ...options });
    this.#items = options.items ? [...options.items] : [];
    this.selectionMode = options.selectionMode ?? "single";
    this.#itemHeight = options.itemHeight ?? null;
    this.#font = options.font ?? null;
    this.itemText = options.itemText ?? ((item) => String(item));
    if (options.selectedIndex !== undefined && options.selectedIndex >= 0) {
      this.#selected.add(options.selectedIndex);
      this.#focusIndex = this.#anchor = options.selectedIndex;
    }
    if (options.onSelectionChanged) this.on("selectionchanged", options.onSelectionChanged);

    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerleave", () => this.#setHover(-1));
    this.on("doubleclick", (e) => this.#onDoubleClick(e));
    this.on("keydown", (e) => this.#onKeyDown(e));
  }

  // --- items ---------------------------------------------------------------

  get items(): readonly T[] {
    return this.#items;
  }
  set items(v: readonly T[]) {
    // Only claim the selection changed if it actually did. Emitting
    // unconditionally lets a handler that reacts by refilling the list drive
    // itself round in circles.
    const hadSelection = this.#selected.size > 0;
    this.#items = [...v];
    this.#selected.clear();
    this.#focusIndex = this.#anchor = -1;
    this.#maxTextWidth = -1;
    this.updateScrollBars();
    this.clampScroll();
    if (hadSelection) this.emit("selectionchanged");
    this.invalidate();
  }

  get count(): number {
    return this.#items.length;
  }

  add(item: T): void {
    this.#items.push(item);
    this.#maxTextWidth = -1;
    this.updateScrollBars();
    this.invalidate();
  }

  removeAt(index: number): void {
    if (index < 0 || index >= this.#items.length) return;
    this.#items.splice(index, 1);
    // Shift every selected index above the removal down by one.
    const next = new Set<number>();
    for (const i of this.#selected) {
      if (i < index) next.add(i);
      else if (i > index) next.add(i - 1);
    }
    this.#selected = next;
    if (this.#focusIndex >= this.#items.length) this.#focusIndex = this.#items.length - 1;
    this.#maxTextWidth = -1;
    this.updateScrollBars();
    this.clampScroll();
    this.emit("selectionchanged");
    this.invalidate();
  }

  clear(): void {
    this.items = [];
  }

  // --- selection -------------------------------------------------------------

  get selectedIndex(): number {
    let min = -1;
    for (const i of this.#selected) if (min < 0 || i < min) min = i;
    return min;
  }
  set selectedIndex(v: number) {
    if (v < 0) {
      this.clearSelection();
      return;
    }
    this.#setSelection([v]);
    this.#focusIndex = this.#anchor = v;
    this.ensureVisible(v);
  }

  get selectedIndices(): number[] {
    return [...this.#selected].sort((a, b) => a - b);
  }
  /**
   * Replace the whole selection.
   *
   * Filtered by `selectionMode` rather than obeyed literally: a caller that
   * hands three indices to a single-selection list should end up in a state the
   * user could also have reached by clicking, since anything else leaves a
   * selection that cannot be reproduced — or undone — by hand.
   */
  set selectedIndices(v: readonly number[]) {
    const wanted =
      this.selectionMode === "none" ? []
      : this.selectionMode === "single" ? v.slice(0, 1)
      : v;
    this.#setSelection(wanted);
    // The lowest index, not the first one given: it is where the block starts,
    // so shift-clicking after a programmatic set extends from the top of it.
    const first = this.selectedIndex;
    this.#focusIndex = this.#anchor = first;
    if (first >= 0) this.ensureVisible(first);
  }

  get selectedItem(): T | undefined {
    const i = this.selectedIndex;
    return i >= 0 ? this.#items[i] : undefined;
  }

  get selectedItems(): T[] {
    return this.selectedIndices.map((i) => this.#items[i]!);
  }

  isSelected(index: number): boolean {
    return this.#selected.has(index);
  }

  clearSelection(): void {
    if (this.#selected.size === 0) return;
    this.#selected.clear();
    this.emit("selectionchanged");
    this.invalidate();
  }

  selectAll(): void {
    if (this.selectionMode !== "multiple") return;
    this.#setSelection(this.#items.map((_, i) => i));
  }

  #setSelection(indices: readonly number[]): void {
    const next = new Set(indices.filter((i) => i >= 0 && i < this.#items.length));
    if (next.size === this.#selected.size && [...next].every((i) => this.#selected.has(i))) return;
    this.#selected = next;
    this.emit("selectionchanged");
    this.invalidate();
  }

  // --- geometry ---------------------------------------------------------------

  get itemHeight(): number {
    return this.#itemHeight ?? this.desktop?.theme.metrics.listItemHeight ?? 20;
  }
  set itemHeight(v: number | null) {
    this.#itemHeight = v;
    this.updateScrollBars();
    this.invalidate();
  }

  protected override onEnvironmentChanged(): void {
    // Font metrics may differ, so the cached widest-item width is stale.
    this.#maxTextWidth = -1;
    super.onEnvironmentChanged();
  }

  protected override contentSize(theme: Theme): Size {
    const pad = theme.metrics.listPadding;
    return {
      width: this.#contentWidth(theme) + pad.left + pad.right,
      height: this.#items.length * this.itemHeight + pad.top + pad.bottom,
    };
  }

  /** Widest item, measured once and cached until the items change. */
  #contentWidth(theme: Theme): number {
    if (this.#maxTextWidth >= 0) return this.#maxTextWidth;
    const desktop = this.desktop;
    if (!desktop) return 0;
    const font = this.#font ?? theme.metrics.font;
    let max = 0;
    for (let i = 0; i < this.#items.length; i++) {
      const w = desktop.rc.measureText(this.itemText(this.#items[i]!, i), font).width;
      if (w > max) max = w;
    }
    this.#maxTextWidth = Math.ceil(max) + theme.metrics.columnPadding * 2;
    return this.#maxTextWidth;
  }

  /** Row index at a point in local coordinates, or -1. */
  indexAt(p: { x: number; y: number }): number {
    const theme = this.desktop?.theme;
    if (!theme) return -1;
    const view = this.viewportRect;
    if (!rectContains(view, p)) return -1;
    const pad = theme.metrics.listPadding;
    const y = p.y - view.y - pad.top + this.scrollOffsetY;
    const index = Math.floor(y / this.itemHeight);
    return index >= 0 && index < this.#items.length ? index : -1;
  }

  /** Row rectangle in content coordinates. */
  itemContentRect(index: number, theme: Theme): Rect {
    const pad = theme.metrics.listPadding;
    return {
      x: 0,
      y: pad.top + index * this.itemHeight,
      width: this.#contentWidth(theme),
      height: this.itemHeight,
    };
  }

  ensureVisible(index: number): void {
    const theme = this.desktop?.theme;
    if (!theme || index < 0 || index >= this.#items.length) return;
    const r = this.itemContentRect(index, theme);
    // Only the vertical axis matters for keyboard navigation.
    this.scrollIntoView({ x: this.scrollOffsetX, y: r.y, width: 0, height: r.height });
  }

  // --- input --------------------------------------------------------------------

  #setHover(index: number): void {
    if (this.#hoverIndex === index) return;
    this.#hoverIndex = index;
    this.invalidate();
  }

  #onPointerDown(e: MouseEventArgs): void {
    if (e.button !== 0 && e.button !== 2) return;
    this.focus();
    const index = this.indexAt({ x: e.x, y: e.y });
    if (index < 0 || this.selectionMode === "none") return;
    e.handled = true;
    this.#focusIndex = index;

    if (this.selectionMode === "multiple" && e.shiftKey && this.#anchor >= 0) {
      const lo = Math.min(this.#anchor, index);
      const hi = Math.max(this.#anchor, index);
      const range: number[] = [];
      for (let i = lo; i <= hi; i++) range.push(i);
      this.#setSelection(e.ctrlKey ? [...this.#selected, ...range] : range);
    } else if (this.selectionMode === "multiple" && e.ctrlKey) {
      const next = new Set(this.#selected);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      this.#anchor = index;
      this.#setSelection([...next]);
    } else {
      this.#anchor = index;
      this.#setSelection([index]);
    }
    this.ensureVisible(index);
  }

  #onPointerMove(e: MouseEventArgs): void {
    this.#setHover(this.indexAt({ x: e.x, y: e.y }));
  }

  #onDoubleClick(e: MouseEventArgs): void {
    const index = this.indexAt({ x: e.x, y: e.y });
    if (index < 0) return;
    this.emit("itemactivate", index);
    e.handled = true;
  }

  #onKeyDown(e: KeyEventArgs): void {
    if (this.#items.length === 0) return;
    const view = this.viewportRect;
    const page = Math.max(1, Math.floor(view.height / this.itemHeight) - 1);
    const current = this.#focusIndex >= 0 ? this.#focusIndex : this.selectedIndex;
    let next = current;

    switch (e.key) {
      case "ArrowUp":
        next = current <= 0 ? 0 : current - 1;
        break;
      case "ArrowDown":
        next = current < 0 ? 0 : Math.min(this.#items.length - 1, current + 1);
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = this.#items.length - 1;
        break;
      case "PageUp":
        next = Math.max(0, (current < 0 ? 0 : current) - page);
        break;
      case "PageDown":
        next = Math.min(this.#items.length - 1, (current < 0 ? 0 : current) + page);
        break;
      case "Enter":
        if (current >= 0) {
          this.emit("itemactivate", current);
          e.handled = true;
        }
        return;
      case " ":
        if (this.selectionMode === "multiple" && current >= 0) {
          const set = new Set(this.#selected);
          if (set.has(current)) set.delete(current);
          else set.add(current);
          this.#setSelection([...set]);
          e.handled = true;
        }
        return;
      case "a":
      case "A":
        if (e.ctrlKey) {
          this.selectAll();
          e.handled = true;
        }
        return;
      default:
        return;
    }

    e.handled = true;
    if (this.selectionMode === "none") {
      this.#focusIndex = next;
      this.ensureVisible(next);
      this.invalidate();
      return;
    }
    this.#focusIndex = next;
    if (this.selectionMode === "multiple" && e.shiftKey && this.#anchor >= 0) {
      const lo = Math.min(this.#anchor, next);
      const hi = Math.max(this.#anchor, next);
      const range: number[] = [];
      for (let i = lo; i <= hi; i++) range.push(i);
      this.#setSelection(range);
    } else {
      this.#anchor = next;
      this.#setSelection([next]);
    }
    this.ensureVisible(next);
  }

  // --- painting -------------------------------------------------------------------

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const state = this.visualState();
    theme.drawListBackground(rc, this.localRect, state);

    const view = this.viewportRect;
    if (view.width <= 0 || view.height <= 0) return;
    const pad = theme.metrics.listPadding;
    const font = this.#font ?? theme.metrics.font;
    const rowHeight = this.itemHeight;
    const active = this.focused || this.#selected.size > 0;

    rc.clipped(view, () => {
      // Virtualize: only the rows overlapping the viewport are drawn.
      const { first, last } = visibleRowRange(
        this.scrollOffsetY,
        view.height,
        rowHeight,
        pad.top,
        this.#items.length,
      );
      const width = Math.max(view.width, this.#contentWidth(theme));

      for (let i = first; i <= last; i++) {
        const row: Rect = {
          x: view.x - this.scrollOffsetX,
          y: view.y + pad.top + i * rowHeight - this.scrollOffsetY,
          width,
          height: rowHeight,
        };
        const rowState: VisualState = {
          ...state,
          hovered: this.#hoverIndex === i,
          selected: this.#selected.has(i),
          active: this.focused,
          focused: this.#focusIndex === i && this.focused,
        };
        theme.drawListItem(rc, row, rowState);
        rc.drawTextInRect(
          this.itemText(this.#items[i]!, i),
          row,
          font,
          theme.listItemTextColor({ ...rowState, active }),
          theme.metrics.columnPadding,
        );
        if (rowState.focused && !rowState.selected) theme.drawFocusRing(rc, row);
      }
    });
  }

  protected override onPaintOverlay(rc: RenderContext, theme: Theme): void {
    this.paintScrollCorner(rc, theme);
  }
}
